// Printer check: errors and warnings shown before save. Pure module (no window/document).
// Issue = { severity: 'error'|'warning', code, message, fieldKey? }  — messages are shown verbatim in the UI.

import { textBox, sampleDateText, isWidthExact, emSize } from './render/metrics.js';
import { barcodeBox, validateData, symbologyById } from './itf/barcode.js';
import { suspiciousOffset } from './itf/dates.js';

export const PRINTER_FONTS = Object.freeze(['Arial', 'Arabic']);
/** Boxes must intersect by more than this (units) on BOTH axes to count as overlapping. */
export const OVERLAP_MIN = 20;
/**
 * Slack (units) on the edge a text/date field's glyph advances run toward (the right edge when unrotated;
 * bottom / left / top at 90 / 180 / 270). Text width comes from glyph advances, which include the last
 * glyph's right side bearing (no ink) and carry model error; CoLOS-made corpus labels sit up to ~30 units
 * past the edge by advance (MOONG DAL "MOONG DAL 1 KG"). Other edges and barcodes are exact.
 * Override with options.textEdgeTolerance.
 */
export const TEXT_EDGE_TOLERANCE = 50;
/**
 * Arial caps start below the top of the text cell: (winAscent − capHeight) / unitsPerEm × em of empty space
 * (≈ 0.19 em). The printer's own files put big titles that far above the label (Y = −250 with a 16 mm font)
 * and print them fine, so that much overshoot at the top edge is not an error for unrotated Arial text.
 */
const ARIAL_CAP_HEIGHT = 1467;
const ARIAL_ASCENT = 1854;
const ARIAL_UNITS_PER_EM = 2048;

const INT32_MIN = -2147483648;
const INT32_MAX = 2147483647;

/** Overshoot in mm with one decimal; tiny overshoots still read as 0.1 mm, never 0.0. */
const mm = (units) => Math.max(0.1, Math.round(Math.abs(units) / 10) / 10).toFixed(1);
const nameOf = (f) => `"${f.name || f.key}"`;
const normDeg = (r) => ((Number(r) || 0) % 360 + 360) % 360;

/** Edge the text advance runs toward for a rotation (SVG rotate, y down): 0 → right, 90 → bottom, … */
const ADVANCE_EDGE = { 0: 'right', 90: 'bottom', 180: 'left', 270: 'top' };

/** The string whose advance sizes a text/date field. */
function shownText(field, options) {
  if (field.kind === 'text') return String(field.text ?? '');
  if (options.textFor) {
    try {
      const t = options.textFor(field);
      if (t != null) return String(t);
    } catch {
      /* invalid offset/format: fall back to the sample below */
    }
  }
  return sampleDateText(field.format);
}

/** Unrotated { w, h } relative to the field origin, or null for fields without known geometry. */
function localSize(field, options) {
  if (field.kind === 'text' || field.kind === 'date') {
    const b = textBox(field, shownText(field, options));
    return { w: b.w, h: b.h };
  }
  if (field.kind === 'barcode') return barcodeBox(field);
  return null;
}

/**
 * Field bounds on the label in units: { x, y, w, h } or null for fields without known geometry
 * (kind 'unsupported'). Same model the canvas renders: SVG `translate(x y) rotate(r)` applied to the local
 * box [0,w]×[0,h], i.e. rotation about the field origin, clockwise on screen (y down):
 *   90°: x ∈ [x−h, x], y ∈ [y, y+w] · 180°: x ∈ [x−w, x], y ∈ [y−h, y] · 270°: x ∈ [x, x+h], y ∈ [y−w, y].
 * UNCONFIRMED: that CoLOS / the printer pivots about the same point (golden files pending).
 * `options.textFor(field)` may supply a date preview string.
 */
export function fieldBounds(field, options = {}) {
  const size = localSize(field, options);
  if (!size) return null;
  const x = Number(field.x) || 0;
  const y = Number(field.y) || 0;
  const deg = normDeg(field.rotation);
  if (deg === 0) return { x, y, w: size.w, h: size.h };
  const r = (deg * Math.PI) / 180;
  const c = Math.round(Math.cos(r) * 1e9) / 1e9;
  const s = Math.round(Math.sin(r) * 1e9) / 1e9;
  // SVG rotate(a) = matrix(cos a, sin a, −sin a, cos a, 0, 0)
  const pts = [[0, 0], [size.w, 0], [size.w, size.h], [0, size.h]].map(([px, py]) => [x + px * c - py * s, y + px * s + py * c]);
  const xs = pts.map((p) => p[0]);
  const ys = pts.map((p) => p[1]);
  const x0 = Math.min(...xs);
  const y0 = Math.min(...ys);
  return { x: x0, y: y0, w: Math.max(...xs) - x0, h: Math.max(...ys) - y0 };
}

/** Problem with an integer property (file values are xs:int), or null. */
function numberProblem(v, what) {
  if (typeof v === 'number' && Number.isInteger(v)) {
    return v < INT32_MIN || v > INT32_MAX ? `${what} is out of range (got ${v})` : null;
  }
  return `${what} must be a whole number (got ${String(v)})`;
}

/**
 * First character that cannot go into a CoLOS text/data value, or null:
 * CR/LF (XSD NonEmptyString ".+" is one line) or anything outside the XML 1.0 Char production
 * (#x9 | #xA | #xD | [#x20-#xD7FF] | [#xE000-#xFFFD] | [#x10000-#x10FFFF]), which includes lone surrogates.
 */
function invalidCharacter(str) {
  for (const ch of String(str ?? '')) {
    const cp = ch.codePointAt(0);
    if (cp === 0x0a || cp === 0x0d) return { lineBreak: true };
    const okXml = cp === 0x09 || (cp >= 0x20 && cp <= 0xd7ff) || (cp >= 0xe000 && cp <= 0xfffd) || (cp >= 0x10000 && cp <= 0x10ffff);
    if (!okXml) return { cp };
  }
  return null;
}
const hex = (cp) => `U+${cp.toString(16).toUpperCase().padStart(4, '0')}`;

/**
 * checkLabel(label, fields, options?)
 * options.isSupportedFormat(str) -> boolean   enables UNVERIFIED_FORMAT for date fields
 * options.barcodeTemplateVerified             false → BARCODE_UNVERIFIED on every barcode
 * options.textFor(field) -> string            date preview text for bounds (else a sample is used)
 * options.textEdgeTolerance                   advance-edge slack for text/date (default TEXT_EDGE_TOLERANCE)
 *
 * Codes — errors: OUT_OF_BOUNDS, DUPLICATE_ID, EMPTY_TEXT, BARCODE_DATA, INVALID_CHARACTERS, INVALID_NUMBER;
 * warnings: OUT_OF_BOUNDS_ESTIMATE, BLANK_TEXT, OVERLAP, DUPLICATE_NAME, FONT_NOT_ON_PRINTER, UNVERIFIED_FORMAT,
 * UNSUPPORTED_FIELD, BARCODE_UNVERIFIED, OVERLAY_BLOCK, OFFSET_UNREALISTIC.
 */
export function checkLabel(label, fields, options = {}) {
  const issues = [];
  const add = (severity, code, message, field) =>
    issues.push(field ? { severity, code, message, fieldKey: field.key } : { severity, code, message });
  const W = Number(label?.width) || 0;
  const H = Number(label?.height) || 0;
  const list = Array.isArray(fields) ? fields : [];

  if (label != null) {
    for (const [key, what] of [['width', 'Label width'], ['height', 'Label height']]) {
      if (label[key] === undefined) continue;
      const p = numberProblem(label[key], what);
      if (p) add('error', 'INVALID_NUMBER', p);
    }
  }

  // Duplicate IDs / names (report every field after the first holder).
  const firstById = new Map();
  const firstByName = new Map();
  for (const f of list) {
    if (f.id != null && f.id !== '') {
      const prior = firstById.get(f.id);
      if (prior) add('error', 'DUPLICATE_ID', `${nameOf(f)} has the same ID (${f.id}) as ${nameOf(prior)}`, f);
      else firstById.set(f.id, f);
    }
    if (f.name) {
      const prior = firstByName.get(f.name);
      if (prior) add('warning', 'DUPLICATE_NAME', `Two fields are named ${nameOf(f)}`, f);
      else firstByName.set(f.name, f);
    }
  }

  const boxes = [];
  for (const f of list) {
    // Integer properties. Geometry problems skip bounds/overlap for this field (they would be meaningless).
    let badGeometry = false;
    const num = (v, what, geometry) => {
      if (v === undefined || v === null) return;
      const p = numberProblem(v, what);
      if (!p) return;
      add('error', 'INVALID_NUMBER', `${nameOf(f)}: ${p}`, f);
      if (geometry) badGeometry = true;
    };
    if (f.id === null || f.id === undefined) add('error', 'INVALID_NUMBER', `${nameOf(f)}: ID is missing or not a whole number`, f);
    else num(f.id, 'ID', false);

    if (f.kind === 'unsupported') {
      add('warning', 'UNSUPPORTED_FIELD', `${nameOf(f)} is kept as-is, not editable`, f);
      continue;
    }

    num(f.x, 'X', true);
    num(f.y, 'Y', true);
    num(f.rotation, 'Rotation', true);
    if (f.kind === 'text' || f.kind === 'date') {
      num(f.font?.height, 'Font height', true);
      num(f.font?.width, 'Font width', true);
    }
    if (f.kind === 'date' && f.offset) {
      num(f.offset.days, 'Offset days', false);
      num(f.offset.months, 'Offset months', false);
      num(f.offset.years, 'Offset years', false);
      num(f.offset.hours, 'Offset hours', false);
      const far = suspiciousOffset(f.offset);
      if (far) add('warning', 'OFFSET_UNREALISTIC', `${nameOf(f)}: ${far}`, f);
    }
    if (f.kind === 'barcode') {
      num(f.barHeight, 'Bar height', true);
      num(f.narrowBarWidth, 'Narrow bar width', true);
      num(f.cellWidth, 'Cell width', true);
    }

    // Characters that cannot be saved / printed on one line.
    const value = f.kind === 'text' ? f.text : f.kind === 'date' ? f.format : f.kind === 'barcode' ? f.data : undefined;
    const badChar = invalidCharacter(value);
    if (badChar) {
      add('error', 'INVALID_CHARACTERS', badChar.lineBreak
        ? `${nameOf(f)} has a line break; a field prints on a single line`
        : `${nameOf(f)} has an invisible character that can't be saved (${hex(badChar.cp)})`, f);
    }

    if (f.kind === 'text') {
      const t = String(f.text ?? '');
      if (!t.length) add('error', 'EMPTY_TEXT', `${nameOf(f)} has no text`, f);
      else if (/^\s+$/.test(t)) add('warning', 'BLANK_TEXT', `${nameOf(f)} is only spaces, so nothing prints`, f);
    }

    if (f.kind === 'text' || f.kind === 'date') {
      const tf = f.font?.typeface;
      if (tf && !PRINTER_FONTS.includes(tf)) add('warning', 'FONT_NOT_ON_PRINTER', `${nameOf(f)} uses font "${tf}", which the printer doesn't have`, f);
    }

    if (f.kind === 'date' && typeof options.isSupportedFormat === 'function' && !options.isSupportedFormat(f.format ?? '')) {
      add('warning', 'UNVERIFIED_FORMAT', `${nameOf(f)} date format "${f.format ?? ''}" hasn't been checked on the printer`, f);
    }

    if (f.kind === 'barcode') {
      if (!badChar) {
        const res = validateData(f.symbology, f.data, { autoCheckDigit: f.autoCheckDigit, errorCorrection: f.errorCorrection });
        if (!res.ok) add('error', 'BARCODE_DATA', `${nameOf(f)}: ${res.message}`, f);
      }
      if (options.barcodeTemplateVerified === false) {
        const symLabel = symbologyById(f.symbology)?.label || 'barcode';
        add('warning', 'BARCODE_UNVERIFIED', `${nameOf(f)} ${symLabel} layout hasn't been test-printed yet`, f);
      }
    }

    // CoLOS DeviceCaps for the SmartDate X65: OverlayMode BLOCK is not supported.
    if (f.overlayMode === 'BLOCK') add('warning', 'OVERLAY_BLOCK', `${nameOf(f)} uses Block overlay, which the printer doesn't support`, f);

    if (badGeometry) continue;
    const b = fieldBounds(f, options);
    if (!b) continue;
    boxes.push({ f, b });

    const isText = f.kind === 'text' || f.kind === 'date';
    const advanceEdge = isText ? ADVANCE_EDGE[normDeg(f.rotation)] : undefined;
    // Width model authoritative? Barcodes and Arial-table text yes; other typefaces are browser estimates.
    const exact = !isText || isWidthExact(f.font, shownText(f, options));
    const ox = Number(f.x) || 0;
    const oy = Number(f.y) || 0;
    const originOff = { left: ox < 0, right: ox > W, top: oy < 0, bottom: oy > H };
    const over = { left: -b.x, right: b.x + b.w - W, top: -b.y, bottom: b.y + b.h - H };
    const past = [];
    const estimated = [];
    for (const side of ['left', 'right', 'top', 'bottom']) {
      let slack = side === advanceEdge ? (options.textEdgeTolerance ?? TEXT_EDGE_TOLERANCE) : 0;
      if (side === 'top' && isText && normDeg(f.rotation) === 0 && f.font?.typeface === 'Arial') {
        slack = Math.max(slack, Math.floor((emSize(f.font) * (ARIAL_ASCENT - ARIAL_CAP_HEIGHT)) / ARIAL_UNITS_PER_EM));
      }
      if (!(over[side] > slack)) continue;
      if (side === advanceEdge && !exact && !originOff[side]) estimated.push(side);
      else past.push([over[side], side]);
    }
    if (past.length) {
      const parts = past.map(([d, side]) => `${mm(d)} mm past the ${side} edge`);
      add('error', 'OUT_OF_BOUNDS', `${nameOf(f)} runs ${parts.join(' and ')}`, f);
    }
    if (estimated.length) {
      const tf = f.font?.typeface;
      const what = tf === 'Arial' ? 'characters not in Arial' : tf ? `${tf} font` : 'this font';
      add('warning', 'OUT_OF_BOUNDS_ESTIMATE', `${nameOf(f)} may run past the ${estimated.join(' and ')} edge (width estimated for ${what})`, f);
    }
  }

  // Overlaps: report on the later (upper) field of each pair.
  for (let i = 0; i < boxes.length; i++) {
    for (let j = i + 1; j < boxes.length; j++) {
      const a = boxes[i].b, c = boxes[j].b;
      const ox = Math.min(a.x + a.w, c.x + c.w) - Math.max(a.x, c.x);
      const oy = Math.min(a.y + a.h, c.y + c.h) - Math.max(a.y, c.y);
      if (ox > OVERLAP_MIN && oy > OVERLAP_MIN) {
        add('warning', 'OVERLAP', `${nameOf(boxes[j].f)} overlaps ${nameOf(boxes[i].f)}`, boxes[j].f);
      }
    }
  }

  // Errors first, otherwise stable.
  return issues
    .map((it, i) => [it, i])
    .sort((p, q) => (p[0].severity === q[0].severity ? p[1] - q[1] : p[0].severity === 'error' ? -1 : 1))
    .map(([it]) => it);
}
